import React, { useEffect, useMemo, useState } from "react";
import "./UploadSummaryModal.scss";

const UploadSummaryModal = ({ data, onClose }) => {
  const [activeTab, setActiveTab] = useState("student");
  const [searchQuery, setSearchQuery] = useState("");

  // role_id 1 = student, 2 = teacher
  const filteredData = useMemo(() => {
    if (!data) return [];
    const roleId = activeTab === "student" ? 1 : 2;
    return data.filter((u) => u.role_id === roleId);
  }, [data, activeTab]);

  const { summary, allDivisions, classTotals, grandTotal } = useMemo(() => {
    if (!filteredData || filteredData.length === 0)
      return { summary: {}, allDivisions: [], classTotals: {}, grandTotal: 0 };

    const summaryMap = {};
    const divisionSet = new Set();

    filteredData.forEach((user) => {
      const userClasses = user?.classes || [];
      if (userClasses.length === 0) {
        const cls = "N/A",
          div = "N/A";
        divisionSet.add(div);
        if (!summaryMap[cls]) summaryMap[cls] = {};
        summaryMap[cls][div] = (summaryMap[cls][div] || 0) + 1;
      } else {
        userClasses.forEach((item) => {
          const cls = item?.grade || "N/A";
          const div = item?.division || "N/A";
          divisionSet.add(div);
          if (!summaryMap[cls]) summaryMap[cls] = {};
          summaryMap[cls][div] = (summaryMap[cls][div] || 0) + 1;
        });
      }
    });

    const sortedDivisions = [...divisionSet].sort();
    const sortedClasses = Object.keys(summaryMap).sort((a, b) => {
      const na = parseInt(a, 10),
        nb = parseInt(b, 10);
      return !isNaN(na) && !isNaN(nb) ? na - nb : a.localeCompare(b);
    });

    const orderedSummary = {};
    const clsTotals = {};
    sortedClasses.forEach((cls) => {
      orderedSummary[cls] = summaryMap[cls];
      clsTotals[cls] = Object.values(summaryMap[cls]).reduce(
        (a, b) => a + b,
        0,
      );
    });

    const gt = Object.values(clsTotals).reduce((a, b) => a + b, 0);
    return {
      summary: orderedSummary,
      allDivisions: sortedDivisions,
      classTotals: clsTotals,
      grandTotal: gt,
    };
  }, [filteredData]);

  const divisionGrandTotals = useMemo(() => {
    const totals = {};
    allDivisions.forEach((div) => {
      totals[div] = Object.values(summary).reduce(
        (acc, cls) => acc + (cls[div] || 0),
        0,
      );
    });
    return totals;
  }, [summary, allDivisions]);

  const isTeacher = activeTab === "teacher";

  const handleSearchFilter = (e) => {
    setSearchQuery(e.target.value);
  };

  const searchedTeachers = useMemo(() => {
    if (!isTeacher) return filteredData;

    const q = searchQuery.toLowerCase().trim();

    if (!q) return filteredData;

    return filteredData.filter((teacher) => {
      const fullName =
        `${teacher?.firstname || ""} ${teacher?.lastname || ""}`.toLowerCase();

      return fullName.includes(q);
    });
  }, [filteredData, searchQuery, isTeacher]);

  return (
    <div className="upload-summary-overlay" onClick={onClose}>
      <div
        className="upload-summary-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="summary-modal-header">
          <div className="summary-header-content">
            <div className="summary-icon-wrapper">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div>
              <h2 className="summary-title">Latest User Upload Summary</h2>
              <p className="summary-subtitle">
                Post-upload segregation by class &amp; division
              </p>
            </div>
          </div>
          <button className="summary-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Toggle */}
        <div className="summary-toggle-bar">
          <button
            className={`toggle-btn ${activeTab === "student" ? "active" : ""}`}
            onClick={() => setActiveTab("student")}
          >
            👤 Students
          </button>
          <button
            className={`toggle-btn ${activeTab === "teacher" ? "active" : ""}`}
            onClick={() => setActiveTab("teacher")}
          >
            🎓 Teachers
          </button>
        </div>

        {/* Stats strip */}
        <div className="summary-total-strip">
          <div className="total-badge">
            <span className="total-label">
              Total {isTeacher ? "Teachers" : "Students"}
            </span>
            <span className="total-number">{filteredData?.length}</span>
          </div>
          {!isTeacher ? (
            <div></div>
          ) : (
            <div className="user-toolbar">
              <input
                className="search-input"
                placeholder="search..."
                onChange={handleSearchFilter}
                value={searchQuery}
              />
            </div>
          )}
        </div>

        {/* Table */}
        <div className="summary-table-container">
          {/* STUDENT TABLE */}
          {!isTeacher ? (
            <table className="summary-table">
              <thead>
                <tr>
                  <th className="class-col">Class</th>

                  {allDivisions.map((div) => (
                    <th key={div} className="division-col">
                      Div {div}
                    </th>
                  ))}

                  <th className="total-col">Total</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(summary).map(([cls, divisions]) => (
                  <tr key={cls}>
                    <td className="class-cell">
                      <span className="class-label">Class {cls}</span>
                    </td>

                    {allDivisions.map((div) => (
                      <td key={div} className="count-cell">
                        <span
                          className={`count-value ${
                            divisions[div] ? "has-value" : "no-value"
                          }`}
                        >
                          {divisions[div] || 0}
                        </span>
                      </td>
                    ))}

                    <td className="row-total-cell">
                      <span className="row-total-value">
                        {classTotals[cls]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="grand-total-row">
                  <td className="class-cell">
                    <span className="grand-total-label">Grand Total</span>
                  </td>

                  {allDivisions.map((div) => (
                    <td key={div} className="count-cell">
                      <span className="grand-total-value">
                        {divisionGrandTotals[div]}
                      </span>
                    </td>
                  ))}

                  <td className="row-total-cell">
                    <span className="grand-total-value highlight">
                      {grandTotal}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            /* TEACHER TABLE */
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Class & Division</th>
                  <th>Subjects</th>
                </tr>
              </thead>

              <tbody>
                {searchedTeachers?.map((teacher) => (
                  <tr key={teacher?.id}>
                    <td>
                      {teacher?.firstname?.charAt(0).toUpperCase() +
                        teacher?.firstname?.slice(1)}{" "}
                      {teacher?.lastname?.charAt(0).toUpperCase() +
                        teacher?.lastname?.slice(1)}
                    </td>

                    <td>
                      {(teacher?.classes || [])
                        .map((c) => `${c?.grade}${c?.division}`)
                        .join(", ")}
                    </td>

                    <td>
                      {[
                        ...new Set(
                          (teacher?.classes || []).flatMap(
                            (c) => c?.subject || [],
                          ),
                        ),
                      ].join(", ") || "no subject added"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="summary-modal-footer">
          <button className="btn-go-dashboard" onClick={onClose}>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadSummaryModal;
